import { convertToLocalDateTime } from "components/helpers";

import { selectColumn } from "components/BaseTableV3/selectColumn";
import TagList from "components/TagList";
import { getCountryByCode } from "helpers/countries";
import { EntityTableActions } from "./EntityTableActions";
import EntityService from "services/Entity";
import CampaignService from "services/Campaign";
import { getUsersForFiltersQuery } from "helpers/user";
import { invalidateEntitiesQuery, deleteEntity } from "hooks/useEntityTable";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import { NULLABLE_OPERATORS } from "components/NewAdvancedSearch/filters/selectOperators";

export const getEntityTableColumns = () => {
  return [
    selectColumn,
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <TableLink href={`/entities/${row.getValue("ID")}`}>
          {row.getValue("ID")}
        </TableLink>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags")}
          modelType="Entity"
          modelId={row.getValue("ID")}
          onUpdate={invalidateEntitiesQuery}
          tableCell
        />
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <TableLink href={`/entities/${row.getValue("ID")}`}>
          {row.getValue("Name")}
        </TableLink>
      ),
    },
    {
      header: "Entity Alternate Names",
      id: "Alternate Names",
      accessorKey: "alternate_names",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Alternate Names").map(({ id, name }) => (
            <li key={`${row.original.id}-${id}`}>{name}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "URLs",
      id: "URLs",
      accessorKey: "urls",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("URLs").map(url => (
            <li key={`${row.original.id}-${url}`}>{url}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Account Manager",
      id: "Account Manager",
      accessorKey: "account_managers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Account Manager").map(manager => (
            <li key={`${row.original.id}-${manager.user_id}`}>
              {manager.user_full_name}
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Last Activity Date",
      id: "Last Activity Date",
      accessorKey: "last_activity_date",
      cell: ({ row }) => (
        <div>
          {row.getValue("Last Activity Date")
            ? convertToLocalDateTime(row.getValue("Last Activity Date")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    {
      header: "Campaigns",
      id: "Campaigns",
      accessorKey: "campaigns",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row
            .getValue("Campaigns")
            .map(({ id: campaignId, name: campaignName }) => (
              <li key={`${row.original.id}-${campaignId}`}>
                <TableLink href={`/campaigns/${campaignId}`}>
                  {campaignName}
                </TableLink>
              </li>
            ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Region",
      id: "Region",
      accessorKey: "region_name",
    },
    {
      header: "Country",
      id: "Country",
      accessorKey: "country",
      cell: ({ row }) => (
        <div>{getCountryByCode(row.getValue("Country"))?.text}</div>
      ),
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <EntityTableActions
          entityId={row.getValue("ID")}
          onConfirmDelete={deleteEntity}
        />
      ),
    },
  ];
};

export const getEntityTableFilters = ({ regions, countries, tags }) => {
  return [
    {
      key: "last_activity_date",
      title: "Last Activity",
      type: "dateRange",
    },
    {
      key: "website",
      title: "Entity URL",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: filters => {
        return EntityService.getWebsitesForFilters(filters);
      },
    },
    {
      key: "search:contact_name",
      title: "Contact Name",
      type: "input",
    },
    {
      key: "search:contact_email",
      title: "Contact Email",
      type: "input",
    },
    {
      key: "search:contact_phone",
      title: "Contact Phone",
      type: "input",
    },
    {
      key: "campaign_ids",
      title: "Campaign",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: CampaignService.getCampaignsForFilters,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "account_manager_id",
      title: "Account Manager",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "assigned_user_id",
      title: "Assignee",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "entity_id",
      title: "Entity",
      type: "ruvixxSelect",
      queryFn: EntityService.getEntitiesForFilters,
    },
    {
      key: "regions",
      title: "Region",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: regions,
    },
    {
      key: "countries",
      title: "Country",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: countries,
    },
  ];
};
